import { AppDate } from '../envTypes';

import { FromTemplate, ModelBase } from './base';
import { Profile } from './profile';
import { DistanceUnit, MassUnit } from './shipping';
import { CollectionLayoutType } from './store';
import { ModerationStatus } from './moderation';

export enum ShippingSurchargeRule {
  PerItem = 'PER_ITEM',
  PerOrderGroup = 'PER_ORDER_GROUP',
}

export interface DimsInfo {
  width?: number;
  height?: number;
  depth?: number;
  unit: DistanceUnit;
}

export interface BaseWeightInfo {
  type: 'singleUnit' | 'poundWithOunce';
}

export interface SingleUnitWeightInfo extends BaseWeightInfo {
  type: 'singleUnit';
  value: number;
  unit: MassUnit;
}

export interface PoundWithOunceWeightInfo extends BaseWeightInfo {
  type: 'poundWithOunce';
  lbValue: number;
  ozValue: number;
}

export type WeightInfo = SingleUnitWeightInfo | PoundWithOunceWeightInfo;

export interface ShippingSurchargeInfo {
  amount: number;
  rule: ShippingSurchargeRule;
}

export enum ListingOriginType {
  Shopify = 'shopify',
}

export interface ListingVariant {
  id: string;
  name: string;
  /** @TJS-type integer */
  quantity: number;
  sku?: string;
  price?: number;
  discountPrice?: number;
  hidden?: boolean;
  platformCost?: number;
  generationCost?: number;
  hostPrice?: number;
  originalInfo?: {
    type: ListingOriginType;
    shopifyVariantId?: string;
    shopifyInventoryLevelId?: string;
  };

  dims?: DimsInfo;
  weight?: WeightInfo;

  commentSoldVariantId?: number;
  skuAttributes?: Record<string, string | null>;
  extraAttributes?: Record<string, string | null>;
  dropshipDetails?: VariantDropshipDetails;
  externalCheckoutUrl?: string;
}

export enum ListingStatus {
  Active = 'active',
  Archived = 'archived',
}

export interface ListingDropshipDetails {
  partnerId: number;
  partnerName: string;
  supplierId: number;
  supplierName: string;
  hasActiveAllocation?: boolean;
  allocationStartDate?: AppDate;
  allocationEndDate?: AppDate;
}

export interface VariantDropshipDetails {
  unitCost: number;
  partnerCommission: number;
  minimumConfigurableRetailPrice: number;
  maximumConfigurableRetailPrice: number;
  suggestedMinimumRetailPrice?: number;
}

export interface GoogleProductCategory extends ModelBase {
  name: string;
  ancestorIds: string[];
  parentId?: string;
  fullName: string;
  enabled: boolean;
  isLeaf: boolean;
  isRoot: boolean;
}

export interface ProductCategory extends ModelBase {
  fullName: string;
  name: string;
  slug?: string;
  sort: number;
  parentId?: string;
  isRoot: boolean;
  enabled: boolean;
  custom: boolean;
  digital?: boolean;
  googleProductCategoryIds: string[];
  isEligibleForWearMe?: boolean;
}

export enum ProductCollectionStrategy {
  ByCategory = 'BY_CATEGORY',
  ManualSelection = 'MANUAL_SELECTION',
}

export interface ProductCollection extends ModelBase, FromTemplate {
  name: string;
  slug?: string;
  sort: number;
  strategy: ProductCollectionStrategy;
  productCategoryIds: string[];
  archived: boolean;
  layoutType?: CollectionLayoutType;
  layoutSort?: number;
}

export enum ListingModelMeProcessStatus {
  Triggered = 'TRIGGERED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
}

export enum ListingType {
  Physical = 'PHYSICAL',
  Digital = 'DIGITAL',
  External = 'EXTERNAL',
  Meeting = 'MEETING',
  Affiliate = 'AFFILIATE',
  Derived = 'DERIVED',
  AvatarMe = 'AVATAR_ME',
  TokenPack = 'TOKEN_PACK',
  AiGeneratedPdf = 'AI_GENERATED_PDF',
  EchoForCommentchat = 'ECHO_FOR_COMMENTCHAT',
  // token gated content defined in /tokenCosts except for avatarme
  TokenGated = 'TOKEN_GATED',
  RealEstate = 'REAL_ESTATE',
  GroupEvent = 'GROUP_EVENT',
}

export enum ListingDerivedFrom {
  Series = 'SERIES',
  Post = 'POST',
  Tip = 'TIP',
}

export interface ListingDerivedFromSeries {
  from: ListingDerivedFrom.Series;
  seriesId: string;
}

export interface ListingDerivedFromPost {
  from: ListingDerivedFrom.Post;
  postId: string;
}

export enum TipRecipientType {
  Seller = 'SELLER',
  Post = 'POST',
  LiveStream = 'LIVE_STREAM',
}

export interface ListingDerivedFromTipToSeller {
  to: TipRecipientType.Seller;
  sellerId: string;
  tippingGoalId?: string;
}

export interface ListingDerivedFromTipToPost {
  to: TipRecipientType.Post;
  postId: string;
  sellerId: string;
}

export interface ListingDerivedFromTipToLiveStream {
  to: TipRecipientType.LiveStream;
  liveStreamId: string;
  sellerId: string;
}

export interface ListingDerivedFromTip {
  from: ListingDerivedFrom.Tip;
  to:
    | ListingDerivedFromTipToSeller
    | ListingDerivedFromTipToPost
    | ListingDerivedFromTipToLiveStream;
}

export enum MeetingRepeatFrequency {
  OneTime = 'ONE_TIME',
  Daily = 'DAILY',
  Weekly = 'WEEKLY',
  Monthly = 'MONTHLY',
  OnWeekdays = 'ON_WEEKDAYS',
}

export enum NamedModeratorUserId {
  Ai = 'AI',
  MalwareScanner = 'MALWARE_SCANNER',
}

export enum WearWithAiListingMediaType {
  Image = 'IMAGE',
  Video = 'VIDEO',
}

export interface WearWithAiListingMedia {
  type: WearWithAiListingMediaType;
  url: string;
}

export interface ListingRating {
  details: Record<string, number | null>;
}

export interface Listing extends FromTemplate {
  name: string;
  slug?: string;
  description?: string;
  price: number;
  discountPrice?: number;
  sku?: string;
  createdAt?: AppDate;
  updatedAt?: AppDate;
  defaultImage: string;
  extraImages?: string[];
  defaultWearWithAiMedia?: WearWithAiListingMedia;
  extraWearWithAiMedias?: WearWithAiListingMedia[];

  sizingGuideImage?: string;
  defaultSizingGuideImage?: string;

  /** @TJS-type integer */
  quantity: number;
  variants?: ListingVariant[];
  /** @TJS-type integer */
  maximumPurchaseQuantity?: number | null;
  maximumPurchaseQuantityPerLiveStream?: number;
  shippingFee?: number | null;
  tags?: string[];
  status?: ListingStatus;

  externalId?: string;
  originalInfo?: {
    type: ListingOriginType;
    shopifyStoreId?: string;
    shopifyLocationId?: string;
    shopifyProductId?: string;
  };

  dims?: DimsInfo;
  weight?: WeightInfo;
  shippingSurcharge?: ShippingSurchargeInfo;

  soldQuantityLast7Days?: number;
  storeFrontName?: string;
  storeCoverImage?: string;

  commentSoldProductId?: number;
  commentSoldVariantId?: number;
  eligibleForModelMe?: boolean;
  shopModelMe?: boolean;
  isLandingEnabled?: boolean;
  skuAttributes?: Record<string, string | null>;
  taxCode?: string;
  nontaxable?: boolean;
  dropshipDetails?: ListingDropshipDetails;
  useLocalPrice?: boolean;

  category?: Omit<ProductCategory, 'createdAt' | 'updatedAt'> & {
    id: string;
  };
  categoryIds?: string[];
  collectionIds?: string[];

  rating?: ListingRating;
  attrsWithLocalUpdates?: {
    name?: boolean;
    price?: boolean;
    description?: boolean;
    image?: boolean;
    category?: boolean;
  };

  modelMeProcessRecord?: { id: string } & ModelMeProcessRecord;
  modelMeImagesStatus?: ListingModelMeImagesStatus;
  version?: number;

  type?: ListingType;
  externalCheckoutUrl?: string;
  externalBrandName?: string;
  externalBrandImage?: string;
  summary?: string;
  discountEnabled?: boolean;
  discountStartDate?: AppDate;
  discountEndDate?: AppDate;
  coverImage?: string;
  thumbnailImage?: string;

  uploadedFile?: {
    filename: string;
    sizeInBytes: number;
    mimeType: string;
    fileHandle: string;
    moderationStatus?: ModerationStatus;
    moderationReason?: string;
  };
  moderationStatus?: ModerationStatus;
  moderatedAt?: AppDate;
  moderatorProfile?: Pick<
    Profile,
    'firstName' | 'lastName' | 'username' | 'avatar'
  > & { fullName: string };
  moderatorUserId?: string;
  adminModerationReason?: string;

  meeting?: {
    startDate: AppDate;
    endDate: AppDate;
    durationInSeconds: number;
    calendarId?: string;
    eventId?: string;
    timeZone?: string;
    repeatFrequency?: MeetingRepeatFrequency;
    repeatCount?: number;
    minDate?: AppDate;
    maxDate?: AppDate;
    availableDates?: number[];
    startTime?: number;
    endTime?: number;
    recurrenceRule?: string;
  };
  internalTags?: Array<
    'AddedToStorefrontBefore' | 'PopStore' /*unreliable*/ | string
  >;
  derivedFrom?:
    | ListingDerivedFromSeries
    | ListingDerivedFromPost
    | ListingDerivedFromTip;
  isEligibleForWearMe?: boolean;
  externalProductCtaText?: string;

  // extra listing attributes
  extra?: Record<string, any>;
}

// immutable
export interface DigitalProductAsset {
  listingId: string;
  createdAt: AppDate;
  fileUrl: string;
  filePath: string;
  mimeType: string;
}

export interface PurchasedDigitalProduct extends ModelBase {
  orderId: string;
  sellerId: string;
  listingId: string;
  buyerId: string;
  filename: string;
  sizeInBytes: number;
  mimeType: string;
  filePath: string;
  signedUrl?: string;
  downloadedCount: number;
  downloadLimitCount?: number;
  downloadEndAt?: AppDate;
}

export enum ListingModelMeImagesStatus {
  Ready = 'READY',
  Approved = 'APPROVED',
  AllApproved = 'ALL_APPROVED',
  AllDismissed = 'ALL_DISMISSED',
}

export interface ModelMeProcessRecord extends ModelBase {
  processId: string; // trigger-modelme-process ID
  storeId: string;
  commentSoldShopId: string;
  email: string; // lower cased
  listingId: string;
  sku: string;
  status: ListingModelMeProcessStatus;
  webhook?: {
    timestamp: AppDate;
    payload: any;
  };
}

// export interface Rating {
//   sellerId: string;
//   orderId: string;
//   listingId: string;
//   buyerId: string;
//   buyerProfile: Profile;
//   rating: number;
//   review?: string;
//   createdAt: AppDate;
//   featured?: boolean;
// }

export interface Rating {
  unifiedSource: string; // userId or (ip + device)
  ip: string;
  device: string;
  score: number;
  userId?: string;
  profile?: Profile; // user profile when available
  message?: string; // optional rating message content
  createdAt: AppDate;
}

// export interface Tip extends ModelBase {
//   orderId: string;
//   sellerId: string;
//   sellerProfile: Profile;
//   buyerId: string;
//   buyerProfile: Profile;
//   amount: number;
//   message?: string;
//   tippingGoalId?: string;
// }

export interface Tip extends ModelBase {
  // always be 'Tip Jar'
  name: string;
  description?: string;
  minimumAmount: number;
  // always be $1
  stepAmount: number;
  defaultImage: string;
  extraImages?: string[];
}

export enum ListingInventoryChangeOperation {
  OrderCreate = 'ORDER_CREATE',
  OrderCreateRevert = 'ORDER_CREATE_REVERT',
  OrderCancel = 'ORDER_CANCEL',
  SendItemCreate = 'SEND_ITEM_CREATE',
  SendItemCreateRevert = 'SEND_ITEM_CREATE_REVERT',
  SendItemClose = 'SEND_ITEM_CLOSE',
  Set = 'SET',
  ListingCreate = 'LISTING_CREATE',
  ListingDELETE = 'LISTING_DELETE',
  OrderClose = 'ORDER_CLOSE',
}

export enum ListingInventorySource {
  Order = 'ORDER',
  User = 'USER',
  SendItem = 'SEND_ITEM',
  CommentSold = 'COMMENT_SOLD',
}

export interface ListingInventoryChange extends ModelBase {
  variantId?: string | null;
  operation: ListingInventoryChangeOperation;
  source?: ListingInventorySource;
  sourceId?: string;
  quantity: number;
  beforeQuantity?: number;
  afterQuantity?: number;
  index: number;
  remark?: string;
}

export enum ListingModelMeImageStatus {
  Ready = 'READY',
  Approved = 'APPROVED',
  Dismissed = 'DISMISSED',
}

export const ImageSourceKey = 'source';
export enum ImageSourceValue {
  ModelMe = 'modelme',
  AvatarMe = 'avatarme',
}

export enum ListingModelMeImageRating {
  One = 1,
  Two = 2,
  Three = 3,
  Four = 4,
  Five = 5,
}

export interface ListingModelMeImageFeedback {
  rating: ListingModelMeImageRating;
  note?: string;
  timestamp: string;
}

export interface ListingModelMeImage extends ModelBase {
  processId: string;
  awsS3Url: string;
  url: string;
  status: ListingModelMeImageStatus;
  feedback?: ListingModelMeImageFeedback;
}

export interface FireListing {
  uuid: string;
  name: string;
  price: string;
  image: string;
}

export interface Purchase {
  buyername: string;
  buyerAvatar?: string;
  sellername: string;
  sellerAvatar?: string;
  listingName: string;
  purchasedAt: AppDate;
  listingUUID: string;
}

// /liveListings/{id}
export interface LiveListing {
  livedAt: AppDate;
  endLiveAt?: AppDate;
  isActive: boolean;
  sellerId: string;
  sellerAvatar?: string;
  listingId: string;
  listing: Listing;
  liveStreamId: string;
  eventId?: string;
  userGroupIds?: string[];
  createdAt: AppDate;
}
